import type { Id } from '@hq/utils';
import { toQuery, type HttpClient } from '../client';
import type {
  AllocateResult,
  ApplyBizStatus,
  ApplyDetail,
  ApplyRow,
  ApproveRequest,
  ApproveResult,
  CreateApplyRequest,
  SubmitApplyRequest,
  SubmitApplyResult
} from '../types/apply';
import type { PageQuery, PageResult } from '../types/result';

const BASE = '/wl/apply';

/** 申领列表查询：分页 + 状态 / 只看我的（后端 ApplyListQuery）。 */
export interface ApplyListQuery extends PageQuery {
  bizStatus?: ApplyBizStatus;
  mine?: boolean;
}

/** 申领（Hq.Api，已实现：POST/GET /api/wl/apply）。path 相对 baseUrl（含 `/api` 段）。 */
export function createApplyApi(http: HttpClient) {
  return {
    /** 游标分页列表；时间筛传 ISO 时间，服务端换算主键区间。 */
    list(page: ApplyListQuery = {}): Promise<PageResult<ApplyRow>> {
      return http.request<PageResult<ApplyRow>>({ path: BASE, query: toQuery(page) });
    },

    detail(id: Id): Promise<ApplyDetail> {
      return http.request<ApplyDetail>({ path: `${BASE}/${id}` });
    },

    create(body: CreateApplyRequest): Promise<ApplyRow> {
      return http.request<ApplyRow>({ path: BASE, method: 'POST', body });
    },

    submit(id: Id, body: SubmitApplyRequest): Promise<SubmitApplyResult> {
      // 空字符串会让后端 ulong? 反序列化失败，未填审批人时直接不传
      const payload = body.approverUserId
        ? body
        : { approverName: body.approverName };
      return http.request<SubmitApplyResult>({ path: `${BASE}/${id}/submit`, method: 'POST', body: payload });
    },

    approve(id: Id, body: ApproveRequest): Promise<ApproveResult> {
      return http.request<ApproveResult>({ path: `${BASE}/${id}/approve`, method: 'POST', body });
    },

    /**
     * 货源判源：院内可用库存 → 外部零库存仓 → 采购缺口，
     * 外仓部分自动生成一张 `GY` 供货指令（幂等，后端 IdempotencyFilter）。
     * 注意：判源会按院内库存预占 `wl_stock_reserve`，出库/签收时核销。
     */
    allocate(id: Id): Promise<AllocateResult> {
      return http.request<AllocateResult>({ path: `${BASE}/${id}/allocate`, method: 'POST' });
    },

    /**
     * 履约推进：审批通过后一步完成「判源（预占）→ 外仓供货指令 / 请购 → 生成配送单」。
     * 幂等（`X-Request-Id`）；已推进过的单据重放返回 `created=false`。
     * 用于审批自动触发失败后的人工重试，也用于「审批通过但未自动建配送」的补救。
     */
    fulfill(id: Id): Promise<FulfillResult> {
      return http.request<FulfillResult>({ path: `${BASE}/${id}/fulfill`, method: 'POST' });
    },

    /** 取消申领并释放预占库存（已签收/已结算不可取消）。 */
    cancel(id: Id, reason?: string): Promise<CancelApplyResult> {
      return http.request<CancelApplyResult>({
        path: `${BASE}/${id}/cancel`,
        method: 'POST',
        body: { reason: reason ?? null }
      });
    }
  };
}

/** 取消申领结果。 */
export interface CancelApplyResult {
  id: string;
  status: string;
  /** 本次释放的预占数量。 */
  releasedQty: number;
}

/** 履约推进结果（后端 `FulfillResult`）。 */
export interface FulfillResult {
  applyId: string;
  bizNo: string;
  /** 推进后状态（ALLOCATED / DELIVERING …）。 */
  status: string;
  /** 判源结论：STORED（院内满足）/ OUTER（外仓供货）/ PURCHASE（转请购）。 */
  allocateResult: string;
  supplyOrderId: string;
  supplyBizNo?: string | null;
  requireId: string;
  requireBizNo?: string | null;
  deliveries: FulfillDelivery[];
  /** false = 本次为重放，未新建下游单据。 */
  created: boolean;
}

export interface FulfillDelivery {
  id: string;
  bizNo: string;
  deliveryType: string;
  status?: string;
}

export type ApplyApi = ReturnType<typeof createApplyApi>;
